import { Spinner } from '@sonnen/web-ui';
import { useAuth } from '@sonnen/web-utils/hooks';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { AppRoute } from '../../constants';
import { useCurrentRoute } from '../../hooks/useCurrentRoute';
import { useTranslation } from '../../i18n/i18n';
import { buildPathWithPersistedSiteId, setRouteAfterLogin } from '../../utils/utils';
import styles from './AuthGuard.module.css';

interface AuthGuardProps {
  children: React.ReactNode;
  allowVisitor?: boolean;
}

export const AuthGuard = ({ children, allowVisitor = false }: AuthGuardProps) => {
  const { authenticated, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const currentRoute = useCurrentRoute();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const loginRedirectTriggeredRef = useRef(false);

  const utmSource = searchParams.get('utm_source');
  const isMobileSession = utmSource?.toLowerCase() === 'mobile';
  const isVisitor = allowVisitor && !user && !authenticated && !isMobileSession;

  useEffect(() => {
    // If visitor, skip auth checks
    if (isVisitor) {
      loginRedirectTriggeredRef.current = false;
      setLoading(false);
      return;
    }

    // If not authenticated, redirect to login and save current route for after login
    if (!authenticated && !user) {
      if (loginRedirectTriggeredRef.current) return;
      loginRedirectTriggeredRef.current = true;
      const routeAfterLogin = buildPathWithPersistedSiteId(currentRoute, location.search);
      setRouteAfterLogin(routeAfterLogin);
      navigate(AppRoute.LOGIN);
    } else if (authenticated && user) {
      // If authenticated, clear any saved route after login
      loginRedirectTriggeredRef.current = false;
      setRouteAfterLogin();
      setLoading(false);
    }
  }, [authenticated, user, isVisitor, currentRoute, location.search, navigate]);

  if (loading) {
    return (
      <div className={styles.spinnerWrapper}>
        <Spinner size="xl" accessibilityLabel={t('loadingUserDetails')} />
      </div>
    );
  }

  return <>{children}</>;
};
